import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Loading from "../../../components/Loading";
import { DepositDetails as DepositDetailsView } from "../DepositCard/DepositDetailsModal";
import { Deposit } from "../DepositCard/deposit-dtos";
import { NotFound } from "../../NotFound";
import client from "../../../client";
import { getCurrencySymbolFromCache } from "../../../utils";
import { MarketplaceActivity } from "./components/MarketplaceActivity";
import { SaleSummary } from "./components/SaleSummary";
import { SplitTraceability } from "./components/SplitTraceability";
import { StockContext } from "./components/StockContext";
import { StockHistory } from "./components/StockHistory";
import { StockActivity, StockTraceability } from "./types";
import { useDepositResource } from "./useDepositResource";
import { depositPagePath } from "./utils";

type DepositDetailsProps = {
  match: { params: { uuid?: string } };
  history?: { push: (path: string) => void };
  readOnly?: boolean;
};

export const DepositDetails = ({ match, history, readOnly = false }: DepositDetailsProps) => {
  const { t } = useTranslation();
  const uuid = match.params.uuid;

  const [deposit, setDeposit] = useState<Deposit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activity = useDepositResource<StockActivity>(uuid, "activity");
  const traceability = useDepositResource<StockTraceability>(uuid, "traceability");

  useEffect(() => {
    if (!uuid) return;

    setLoading(true);
    setError(null);

    client
      .get(`/api/deposits/${uuid}/`, { params: { with_status: true, with_location: true } })
      .then(response => {
        setDeposit(response.data);
      })
      .catch(() => {
        setError(t("farmersMarket.depositCard.eventsError"));
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const events = Array.isArray(activity.data?.events) ? activity.data.events : [];
  const trace = traceability.data?.stock && traceability.data?.split ? traceability.data : null;
  const stockIdByUuid = useMemo(
    () => Object.fromEntries((trace?.split.pieces ?? []).map(piece => [piece.uuid, piece.id])),
    [trace]
  );

  if (loading) {
    return <Loading isVisible>{t("farmersMarket.depositCard.eventsLoading")}</Loading>;
  }

  if (error || !deposit) {
    return <NotFound />;
  }

  const currency = getCurrencySymbolFromCache() ?? "";
  const unit = deposit.unit_of_measure?.code;

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <DepositDetailsView
        deposit={deposit}
        onClickAggregatorDetails={id =>
          history?.push(
            readOnly ? `/wfpManager/aggregators/${id}` : `/representative/market/aggregator/${id}`
          )
        }
        isOpen={true}
        variant="page"
      />
      <StockHistory events={events} status={deposit.stock_status} unit={unit} currency={currency} />
      {trace?.sale && <SaleSummary sale={trace.sale} currency={currency} />}
      {trace && (
        <SplitTraceability
          split={trace.split}
          unit={unit}
          pagePath={pieceUuid => depositPagePath(readOnly, pieceUuid)}
        />
      )}
      <MarketplaceActivity
        events={events}
        isLoading={activity.isLoading}
        hasError={activity.hasError}
        stockIdByUuid={stockIdByUuid}
      />
      {trace && <StockContext traceability={trace} />}
    </main>
  );
};

export const WfpManagerDepositDetails = (props: DepositDetailsProps) => (
  <DepositDetails {...props} readOnly />
);
